import React, {useState, useEffect} from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaIdCard,
  FaBriefcase,
  FaTools,
  FaMoneyBillWave,
} from "react-icons/fa";

import "../styles/BusinessDetails.css";

import * as subscriptionService from "../_services/subscription.service.js";

import { toBoolean } from "../_utils/to.boolean.js";
import { toast } from "react-hot-toast";

import formatDate from "../_utils/formatDate.js";
import formatCurrency from "../_utils/formatCurrency.js";

const months = [
  {value:1, name: "1 Month"},
  {value:2, name: "2 Months"},
  {value:3, name: "3 Months"},
  {value:4, name: "4 Months"},
  {value:5, name: "5 Months"},
  {value:6, name: "6 Months"},
  {value:7, name: "7 Months"},
  {value:8, name: "8 Months"},
  {value:9, name: "9 Months"},
  {value:10, name: "10 Months"},
  {value:11, name: "11 Months"},
  {value:12, name: "Annual Subscription"},
]

export default function BusinessSubscriptions({ 
  tenant, 
  plan 
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [errors, setErrors] = useState({});
  const [amount, setAmount] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(12);
   
  const calculateAmount = () => {
    if(!plan) return;
    if(Number(selectedMonth) === 12) {
      setAmount(Number(plan.annualPrice));
    } else {
      const total = Number(selectedMonth) * Number(plan.monthlyPrice);
      setAmount(Number(total));
    }
  };

  const calculateDiscount = () => {
    if(!plan) return;
    const actual = Number(plan.monthlyPrice) * 12;
    setDiscount(Number(actual) - Number(plan.annualPrice));
  }

  useEffect(() => {
    calculateAmount();
    calculateDiscount();
  }, [selectedMonth])

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
  
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
  
    setLoading(true);
    setValidated(true);
  
    try {
      const result = await subscriptionService.save({planId: plan.planId, tenantId: tenant.tenantId, months:selectedMonth})
      toast.success(result.message || "Process complete");
      navigate(`/business?tenantId=${tenant.tenantId}&target=invoices`, {replace:true});      
    } catch (error) {
      toast.error(error.message || `Something went wrong. Please try again.`);
    } finally {
      setLoading(false)
    }
  };
    
  if (!loading && !tenant) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          Invalid business details
        </div>
      </div>
    );
  }

  const initials = tenant.businessName
    ?.split(" ")
    .map((x) => x[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const active = tenant.subscriptions.find(
    sub => sub.isActive === 1
  );
    
  const pending = tenant.subscriptions.find(
    sub => sub.status === "Pending"
  );
  
  return (
    <div className="business-details-page">

      <div className="business-header shadow">
        <div className="business-avatar">
          {initials}
        </div>

        <div className="business-header-info">
          <h2>{tenant.businessName}</h2>
          <span className="solution-badge">
            {
              active
              ? `${active.productName} - ${active.planName}`
              : "No active Subscription"
            }
          </span>
        </div>
      </div>

      <div className="row g-4 mt-1">

        <div className="col-lg-6">
          <div className="flex wrap g-4">
            <BusinessItem
              icon={<FaBriefcase />}
              label="Subscription"
              value={
                active
                ? `${active.productName} - ${active.planName}`
                : "No active Subscription"
              }
            />
            <hr/>
            <div className="card-title m-2">
              Subscription History
            </div>
              {
                tenant.subscriptions.length 
                ? 
                  tenant.subscriptions.map((sub) => (
                    <BusinessItem
                      icon={<FaBriefcase />}
                      label={`Expired: ${formatDate(sub.expiryDate, "long")} @ ${formatDate(sub.expiryDate, "time")}`}
                      value={`${sub.productName} - ${sub.planName}`}
                    />
                  ))
                : <p>This business has not subscribed to any products yet</p>
              }
          </div>
        </div>

        <div className="col-lg-6">           
              {
                pending 
                ? 
                  <>
                    <BusinessItem
                      icon={<FaBriefcase />}
                      label="Pending Invoice"
                      value={`${pending.productName} - ${pending.planName}`}
                      onClick={() =>
                        navigate(
                          `/business/?tenantId=${tenant.tenantId}&target=invoices`, 
                          {replace:true, state:{tenant}}
                        )
                      }
                    />
                  </>
                  
                : 
                  <>
                <div className="business-card"> 
                  <div className="card-title m-2">
                    Add Subscription
                  </div>
                  <div className="d-flex wrap gap-4">
                    {
                      active && !plan && (
                        <button
                          className="btn-primary btn"
                          onClick={() => 
                            navigate(`/solutions?proceedTo=subscribe&tenantId=${tenant.tenantId}`)
                          }
                        >
                          Renew
                        </button>
                      )
                    }
                    {
                      !plan && (
                        <button
                          className="btn-primary btn"
                          onClick={() => 
                            navigate(`/solutions?proceedTo=subscribe&tenantId=${tenant.tenantId}`)
                          }
                        >
                          Choose Product
                        </button>
                      )
                    }
                  </div>

                    {
                      plan && (
                        <div>
                          <BusinessItem
                            icon={<FaTools />}
                            label="ProductName"
                            value={`${plan.productName} - ${plan.planName}`}
                          />
                          <BusinessItem
                            icon={<FaMoneyBillWave />}
                            label="Monthly Subscription"
                            value={formatCurrency(plan.monthlyPrice, plan.currency, true)}
                          />
                          <BusinessItem
                            icon={<FaMoneyBillWave />}
                            label="Annual Subscription"
                            value={formatCurrency(plan.annualPrice, plan.currency, true)}
                          />
                          
                          <form onSubmit={handleSubmit} className="m-2">
                            <div className="form-group">
                              <label className="form-label"> Choose Period</label>
                              <select
                                className="form-control"
                                name="months"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                              >
                                {
                                  months.map(m => (
                                    <option key={m.value} value={m.value}>{m.name}</option>
                                  ))
                                }
                              </select>
                            </div>
                            
                            <BusinessItem
                              icon={<FaMoneyBillWave />}
                              label="Invoice Total"
                              value={
                                `${formatCurrency(amount, plan.currency, true)} ` +
                                `${
                                  discount > 0 && Number(selectedMonth) === 12
                                  ? ` Save ${formatCurrency(discount, plan.currency, true)}`
                                  : ''
                                }`
                              }
                            />

                            <button 
                              disabled={loading}
                              type="submit"
                              className="btn btn-primary"
                            >
                              {
                                loading 
                                ? <i className="spinner"></i>
                                : <>Save Subscription</>
                              }
                            </button>
                          </form>
                        </div>
                      )
                    }
                    </div>
                  </>
              }

        </div>

      </div>

    </div>
  );
}

function BusinessItem({ icon, label, value, onClick = null  }) {
  return (
    <div className="business-item"
      onClick={
        onClick 
        ? onClick
        : () => {}
      }
    >
      <div className="business-icon">
        {icon}
      </div>

      <div className="business-content">
        <small>{label}</small>
        <h6>{value || "-"}</h6>
      </div>
      {
        onClick && (
          <button 
            onClick={onClick}
            className="btn"
          >
            <i className="bi bi-arrow-right"></i>
          </button>
        )
      }
    </div>
  );
}